import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DataSourceOrdenacaoInsensivel } from '../../../../shared/helpers/ordenacao-tabela.helper';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { MotivoTransicaoService } from '../../../../shared/services/apis/vendas/motivo-transicao.service';
import { MotivoTransicao } from '../../../../shared/services/models/vendas/motivo-transicao.model';
import { MotivoTransicaoAtivoRequest } from '../../../../shared/services/requests/vendas/motivo-transicao-ativo.request';
import { UtilService } from '../../../../shared/services/util.service';

@Component({
  selector: 'app-motivos-transicao',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatBadgeModule,
    MatDialogModule,
    MatSelectModule,
    MatTooltipModule,
    EmptyListComponent,
    HeaderComponent,
    LoadingBoxDefaultListComponent,
  ],
  templateUrl: './motivos-transicao.component.html',
  styleUrl: './motivos-transicao.component.css',
})
export class MotivosTransicaoComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Vendas;
  public model: FormGroup;
  public _paginaAtual: number = 0;
  public _tamanhoPagina: number = 20;
  public _totalRegistros: number = 0;
  public _dataSource = new DataSourceOrdenacaoInsensivel<MotivoTransicao>();
  public _isLoading: boolean = false;
  public _temFiltroAplicado: boolean = false;
  // Snapshot dos filtros EFETIVAMENTE aplicados. O painel de filtros escreve direto no `model`,
  // então ler o FormGroup ao vivo faria uma seleção não aplicada vazar para o "Ver Mais" —
  // paginando com filtro diferente do que está na tela, o que duplica linhas e corrompe o total.
  private _nomeAplicado: string = '';
  private _isAtivoAplicado: boolean | null = null;
  public displayedColumns: string[] = ['select', 'nome', 'situacao', 'actions'];
  public selection = new SelectionModel<MotivoTransicao>(true, []);

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._dataSource.sort = sort;
  }

  constructor(
    private formBuilder: FormBuilder,
    private motivoTransicaoService: MotivoTransicaoService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private utilService: UtilService
  ) {}

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      pesquisa: [''],
      isAtivo: [null],
    });
    this.loadFilters();
    this.capturarFiltros();
    this.carregarDados();
  }

  /** Congela os filtros do formulário como "aplicados". Chamado só quando a lista é resetada. */
  private capturarFiltros(): void {
    this._nomeAplicado = this.model.get('pesquisa')?.value || '';
    this._isAtivoAplicado = this.model.get('isAtivo')?.value ?? null;
    this._temFiltroAplicado = !!this._nomeAplicado || this._isAtivoAplicado !== null;
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/configuracoes/vendas/motivos-transicao/cadastrar',
    ]);
  }

  private loadFilters(): void {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) this.model.patchValue(filtroSalvo);
  }

  // --- Pesquisa e paginação ---

  public carregarDados(reset: boolean = false): void {
    this._isLoading = true;
    const nome: string = this._nomeAplicado;
    const isAtivo: boolean | null = this._isAtivoAplicado;

    this.motivoTransicaoService.getAll(nome, isAtivo, this._paginaAtual, this._tamanhoPagina).subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show(
            'Erro',
            response.errorDescription || 'Erro ao carregar os motivos de transição',
            AlertType.error
          );
          return;
        }
        this._totalRegistros = response.totalRegistros;
        if (reset) {
          this._dataSource.data = response.listaMotivos;
        } else {
          this._dataSource.data = [...this._dataSource.data, ...response.listaMotivos];
        }
      },
      error: err => {
        this._isLoading = false;
        this.alertService.show(
          'Erro inesperado',
          err.message || 'Erro ao carregar os motivos de transição',
          AlertType.error
        );
      },
    });
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.carregarDados();
  }

  public clickPesquisar(): void {
    this._paginaAtual = 0;
    this.selection.clear();
    this._dataSource.data = [];
    this.capturarFiltros();
    this.carregarDados(true);
  }

  public aplicarFiltros(): void {
    this.clickPesquisar();
  }

  public limparFiltrosAvancados(): void {
    this.model.patchValue({ isAtivo: null });
    this.clickPesquisar();
  }

  public limparFiltros(): void {
    this.model.patchValue({ pesquisa: '', isAtivo: null });
    this.clickPesquisar();
  }

  public get qtdFiltrosAvancados(): number {
    // Conta os filtros APLICADOS, não os digitados no painel — o badge tem que refletir o que
    // está de fato filtrando o grid.
    let count = 0;
    if (this._isAtivoAplicado !== null) count++;
    return count;
  }

  // --- Seleção (checkbox) ---

  public isAllSelected(): boolean {
    return (
      this.selection.selected.length === this._dataSource.data.length && this._dataSource.data.length > 0
    );
  }

  public toggleAllRows(): void {
    this.isAllSelected() ? this.selection.clear() : this.selection.select(...this._dataSource.data);
  }

  public get selecionadosExcluiveis(): MotivoTransicao[] {
    return this.selection.selected.filter(item => !item.emUso);
  }

  // --- Ativar / Desativar ---

  public toggleAtivo(item: MotivoTransicao): void {
    this._isLoading = true;
    const request = new MotivoTransicaoAtivoRequest();
    request.isAtivo = !item.isAtivo;

    this.motivoTransicaoService.toggleAtivo(item.idMotivoTransicao, request).subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }
        this.alertService.show(
          'Sucesso',
          `Motivo ${request.isAtivo ? 'ativado' : 'desativado'} com sucesso.`,
          AlertType.success
        );
        this.clickPesquisar();
      },
      error: err => {
        this._isLoading = false;
        this.alertService.show('Erro', err.message || 'Erro ao alterar a situação do motivo', AlertType.error);
      },
    });
  }

  // --- Exclusão ---

  public tooltipExclusao(item: MotivoTransicao): string {
    return `'${item.nome}' já foi usado em documentos. Desative-o para que não apareça em novas movimentações, preservando o histórico.`;
  }

  public abrirModalExcluir(item?: MotivoTransicao): void {
    const isBulk = !item;
    const itemsToDelete = isBulk ? this.selecionadosExcluiveis : [item!];

    if (isBulk && this.selection.selected.length > 0 && itemsToDelete.length === 0) {
      this.alertService.show(
        'Nada a excluir',
        'Os motivos selecionados já foram usados em documentos. Desative-os para preservar o histórico.',
        AlertType.warning
      );
      return;
    }

    if (itemsToDelete.length === 0 || (!isBulk && item!.emUso)) return;

    const descricao = isBulk
      ? `${itemsToDelete.length} motivo(s) de transição serão excluídos permanentemente. Esta ação não pode ser desfeita. Confirma a exclusão?`
      : `O motivo '${item!.nome}' será excluído permanentemente. Esta ação não pode ser desfeita. Confirma a exclusão?`;

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmar exclusão',
        description: descricao,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.confirm) return;

      this._isLoading = true;
      if (isBulk) {
        this.motivoTransicaoService.deleteBulk(itemsToDelete.map(i => i.idMotivoTransicao)).subscribe({
          next: r => {
            this._isLoading = false;
            if (r.isError) {
              this.alertService.show('Atenção', r.errorDescription, AlertType.warning);
              return;
            }
            this.alertService.show('Sucesso', 'Motivos excluídos com sucesso.', AlertType.success);
            this.selection.clear();
            this.clickPesquisar();
          },
          error: err => {
            this._isLoading = false;
            this.alertService.show('Erro', err.message || 'Erro ao excluir os motivos', AlertType.error);
          },
        });
      } else {
        this.motivoTransicaoService.delete(item!.idMotivoTransicao).subscribe({
          next: r => {
            this._isLoading = false;
            if (r.isError) {
              this.alertService.show('Erro', r.errorDescription, AlertType.error);
              return;
            }
            this.alertService.show('Sucesso', 'Motivo excluído com sucesso.', AlertType.success);
            this.clickPesquisar();
          },
          error: err => {
            this._isLoading = false;
            this.alertService.show('Erro', err.message || 'Erro ao excluir o motivo', AlertType.error);
          },
        });
      }
    });
  }
}
